import { useContext, useEffect, useMemo, useState } from "react";
import { GlobalStateContext } from "../../../layouts/GlobalStates";
import { useRequest } from "../../../hooks/helperData/useRequest";
import { get } from "../../../controlers/global/ConfigRequest";
import {
    COMPANY_CLUBS,
    COMPANY_DETAIL,
    COMPANY_MEMBERS,
    GET_AUTOMATION_GROUP_LIST,
    GET_COMPANY_ROLES_LIST,
    SETTING_FORMS_LIST,
    SETTING_ITEMS,
    SETTING_ITEMS_TYPE_TREE,
    SURVEY_LIST,
} from "../../../helperData/ApiAddress";
import { isOrganizationMenuEnabled, isParticipationEnabled } from "../../../helperData/organizationSidebar";
import {
    countTreeNodes,
    isProfileComplete,
    listCount,
    profileFieldCount,
} from "../setupUtils";

const emptyCounts = {
    itemCount: 0,
    itemTypeCount: 0,
    formCount: 0,
    handlingGroupCount: 0,
    roleCount: 0,
    memberCount: 0,
    clubMemberCount: 0,
    surveyCount: 0,
    surveyResponseCount: 0,
};

export default function useSetupSnapshot(decisionSignals) {
    const { auth } = useContext(GlobalStateContext);
    const companyId = auth?.usercompany || "";

    const [getItems] = useRequest(SETTING_ITEMS, "GET");
    const [getItemTypes] = useRequest(SETTING_ITEMS_TYPE_TREE, "GET");
    const [getForms] = useRequest(SETTING_FORMS_LIST, "GET");
    const [getGroups] = useRequest(
        GET_AUTOMATION_GROUP_LIST.replace("{id}", companyId),
        "GET"
    );
    const [getRoles] = useRequest(GET_COMPANY_ROLES_LIST, "GET");
    const [getSurveys] = useRequest(SURVEY_LIST, "GET");
    const [getClubs] = useRequest(
        COMPANY_CLUBS.replace("{id}/", "1"),
        "GET"
    );
    const [getMembers] = useRequest(COMPANY_MEMBERS, "GET");

    const [company, setCompany] = useState(null);
    const [counts, setCounts] = useState(emptyCounts);
    const [loading, setLoading] = useState(true);

    const clubEnabled = isParticipationEnabled(auth);
    const surveyEnabled = isOrganizationMenuEnabled(
        auth,
        "survey",
        auth?.company_admin && auth?.package?.survey_permission
    );
    const settingsEnabled = isOrganizationMenuEnabled(
        auth,
        "settings",
        auth?.company_admin && auth?.package?.setting_permission
    );

    useEffect(() => {
        let cancelled = false;

        if (!companyId) {
            setCompany(null);
            return undefined;
        }

        get(COMPANY_DETAIL.replace("{id}", companyId))
            .then((detail) => {
                if (!cancelled) setCompany(detail);
            })
            .catch(() => {
                if (!cancelled) setCompany(null);
            });

        return () => {
            cancelled = true;
        };
    }, [companyId]);

    useEffect(() => {
        let cancelled = false;

        const settle = (promise) => promise.catch(() => null);

        Promise.all([
            settle(getItems({ page: 1, page_size: 1 })),
            settle(getItemTypes()),
            settle(getForms()),
            companyId ? settle(getGroups()) : Promise.resolve(null),
            settle(getRoles()),
            surveyEnabled
                ? settle(getSurveys({ page: 1, page_size: 20 }))
                : Promise.resolve(null),
            clubEnabled ? settle(getClubs()) : Promise.resolve(null),
            companyId
                ? settle(getMembers({ page: 1, company: companyId }))
                : Promise.resolve(null),
        ]).then(
            ([
                items,
                tree,
                forms,
                groups,
                roles,
                surveys,
                clubs,
                members,
            ]) => {
                if (cancelled) return;

                const surveyResults = Array.isArray(surveys?.results)
                    ? surveys.results
                    : Array.isArray(surveys)
                    ? surveys
                    : [];

                setCounts({
                    itemCount: listCount(items),
                    itemTypeCount: countTreeNodes(tree),
                    formCount: listCount(forms),
                    handlingGroupCount: listCount(groups),
                    roleCount: listCount(roles),
                    memberCount: listCount(members),
                    clubMemberCount: listCount(clubs),
                    surveyCount: listCount(surveys),
                    surveyResponseCount: surveyResults.reduce(
                        (sum, survey) =>
                            sum + (Number(survey.response_count) || 0),
                        0
                    ),
                });
                setLoading(false);
            }
        );

        return () => {
            cancelled = true;
        };
        // Fetch once on mount; company-scoped APIs read the session user.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    const snapshot = useMemo(() => {
        const profileDone = profileFieldCount(company);
        const profileComplete = isProfileComplete(company);
        const stages = [
            {
                id: "profile",
                labelKey: "setup.stage.profile",
                complete: profileComplete,
                href: "/dashboard/profile",
                count: profileDone,
                total: 5,
            },
            settingsEnabled && {
                id: "itemtypes",
                labelKey: "setup.stage.itemtypes",
                complete: counts.itemTypeCount > 0,
                href: "/dashboard/exp-settings",
                count: counts.itemTypeCount,
            },
            settingsEnabled && {
                id: "items",
                labelKey: "setup.stage.items",
                complete: counts.itemCount > 0,
                href: "/dashboard/exp-settings",
                count: counts.itemCount,
            },
            settingsEnabled && {
                id: "forms",
                labelKey: "setup.stage.forms",
                complete: counts.formCount > 0,
                href: "/dashboard/exp-settings",
                count: counts.formCount,
            },
            settingsEnabled && {
                id: "handling",
                labelKey: "setup.stage.handling",
                complete: counts.handlingGroupCount > 0,
                href: "/dashboard/exp-settings",
                count: counts.handlingGroupCount,
            },
            clubEnabled && {
                id: "participation",
                labelKey: "setup.stage.participation",
                complete: counts.clubMemberCount > 0,
                href: "/dashboard/customer-club",
                count: counts.clubMemberCount,
            },
            surveyEnabled && {
                id: "surveys",
                labelKey: "setup.stage.surveys",
                complete: counts.surveyCount > 0,
                href: "/dashboard/surveys",
                count: counts.surveyCount,
            },
        ].filter(Boolean);

        const completedCount = stages.filter((stage) => stage.complete).length;
        const messageCount = Number(decisionSignals?.messageCount) || 0;
        const reviewCount = Number(decisionSignals?.reviewCount) || 0;

        return {
            loading,
            ...counts,
            profileDone,
            stages,
            completedCount,
            totalStages: stages.length,
            percent:
                stages.length === 0
                    ? 0
                    : Math.round((completedCount / stages.length) * 100),
            hasDecisionData:
                messageCount > 0 ||
                reviewCount > 0 ||
                counts.surveyResponseCount > 0,
            canSuggest:
                messageCount > 0 ||
                reviewCount > 0 ||
                counts.surveyResponseCount > 0 ||
                counts.itemCount > 0 ||
                counts.itemTypeCount > 0,
            incompleteStages: stages.filter((stage) => !stage.complete),
        };
    }, [
        clubEnabled,
        company,
        counts,
        decisionSignals?.messageCount,
        decisionSignals?.reviewCount,
        loading,
        settingsEnabled,
        surveyEnabled,
    ]);

    return snapshot;
}
